import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";

import type { CardProfileEntity } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileEntity";
import { type ExtendedColumnsType, useUpdateTableDataMutation } from "../../../../../../../components";
import { CSVImportButton } from "../../../../../../../components/FeatureButtons/CSVImportButton";
import { type AntCustomFormField, type CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../../../../../components/NotificationAlerts/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import { displayEnumValue } from "../../../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { Actions } from "../../../../../../../components/Table/types/types";
import { getAssignedDataGroup } from "../../../../../../../core/router/auth";
import { canAccess } from "../../../../../../../core/router/auth/permissions";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../../../core/service/baseApi";
import { showSuccessNotification } from "../../../../../../../core/setup/redux/state/notifications/notificationsState";
import type { Entity } from "../../../../../../../core/types/fe";
import { CardTypesSelector } from "./CardTypesSelector";

export interface AppProfileCardProfilesTableProps extends AntCustomFormField<CardProfileEntity[]>, CommonFieldAttributes {}

export const AppProfileCardProfilesTable: FC<AppProfileCardProfilesTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const appendRowId = (array: CardProfileEntity[]) =>
        // sort items in array by id number
        array
            .slice()
            .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
            .map((item, idx) => ({ ...item, rowId: idx }));

    const location = useLocation();
    const isApac = location.pathname.includes("/apac");
    const hasAccess = canAccess(ResourceName.APPLICATION_PROFILE_LISTS, "ePayProductCsvImport");

    const getColumnsConfig = (t: Function): ExtendedColumnsType<CardProfileEntity>[] => {
        return [
            {
                title: t("applicationProfileCreateForm.table.id"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("applicationProfileCreateForm.table.name"),
                key: "name",
                dataIndex: "name",
            },
            {
                title: t("applicationProfileCreateForm.table.type"),
                key: "type",
                dataIndex: "type",
                render: (cellValue) => displayEnumValue(cellValue, "application-profile-type-enum"),
            },
            {
                title: t("applicationProfileCreateForm.table.cardType"),
                key: "cardType",
                dataIndex: "cardType",
            },
            {
                title: t("applicationProfileCreateForm.table.panRanges"),
                key: "panRanges",
                dataIndex: "panRange",
            },
            {
                title: t("applicationProfileCreateForm.table.aidRanges"),
                key: "aidRanges",
                dataIndex: "aidRange",
            },
        ];
    };

    const onDataChanged = useCallback(
        (data: CardProfileEntity[]) => {
            if (onChange) {
                onChange(data);
            }
        },
        [onChange],
    );

    const [updateTable] = useUpdateTableDataMutation();
    const dataGroup = getAssignedDataGroup();

    const notifyOkDelete = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: "Card Profile",
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const deleteCallback = (item: CardProfileEntity) => {
        updateTable({
            url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.APPLICATION_PROFILE_LISTS}/${id}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION}/${item.id}`,
            method: "DELETE",
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performDeleteSuccess = (payload: AlertActionPayload | undefined) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };
    const copyCallback = (record: CardProfileEntity) => {
        updateTable({
            url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.APPLICATION_PROFILE_LISTS}/${id}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION}/${record.id}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            const payloadIdObject = payload as Entity;
            const successRoute = isApac
                ? `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${id}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_APAC}/${FORM_ACTION.EDIT}/${payloadIdObject.id}?type=${record.type}`
                : `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${id}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_EFT4F}/${FORM_ACTION.EDIT}/${payloadIdObject.id}?type=${record.type}`;
            navigate(successRoute);
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    };

    const onRowClick = (record: CardProfileEntity) => {
        const route = isApac
            ? `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${id}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_APAC}/${FORM_ACTION.EDIT}/${record.id}?type=${record.type}`
            : `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${id}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_EFT4F}/${FORM_ACTION.EDIT}/${record.id}?type=${record.type}`;
        navigate(route);
    };

    const handleImportSuccess = useCallback(() => {
        // refetch data. RTK Query cache invalidation triggers refetch.
        dispatch(baseAuthApi.util.invalidateTags([ResourceName.APPLICATION_PROFILE_LISTS]));
    }, [dispatch]);

    return (
        <PropertyTable<CardProfileEntity>
            name="AppProfileCardProfiles_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="rowId"
            tableData={appendRowId(value) || []}
            readOnly={readOnly || false}
            elementSelector={<CardTypesSelector isApac={isApac} />}
            onDataChanged={onDataChanged}
            actions={[Actions.DELETE, Actions.COPY]}
            customActions={[
                <CSVImportButton
                    key="csv-import-btn"
                    translationKey="list.importEPay"
                    resourceName={ResourceName.APPLICATION_PROFILE_LISTS}
                    isDisabled={!hasAccess}
                    maxRows={1000}
                    id={id}
                    onSuccess={handleImportSuccess}
                />,
            ]}
            actionCallbacks={{ delete: (item?: CardProfileEntity) => item && deleteCallback(item), copy: copyCallback }}
            onRow={(record: CardProfileEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
        />
    );
};
